<template>
  <div class="features-container">
    <!-- Hero Section delle Features -->
    <section class="features-hero py-7 bg-primary text-white">
      <div class="hero-pattern position-absolute w-100 h-100"></div>
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h1 class="display-4 fw-bold mb-4">Gestione Completa Auto e Immobili</h1>
            <p class="lead mb-4">
              Un'unica piattaforma per gestire il tuo business automotive e immobiliare nel FVG.
              Semplifica i processi, aumenta l'efficienza e monitora tutto in tempo reale.
            </p>
          </div>
          <div class="col-lg-6">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"
              alt="Gestione Completa Auto e Immobili"
              class="img-fluid rounded shadow"
            >
          </div>
        </div>
      </div>
    </section>

    <!-- Dashboard Preview (quella che avevamo già) -->
    <section class="dashboard-preview py-7 bg-light">
      <div class="container">
        <div class="text-center mb-5">
          <span class="badge bg-primary-subtle text-primary mb-3">Dashboard</span>
          <h2 class="display-5 fw-bold mb-3">Tutto Sotto Controllo</h2>
          <p class="lead text-muted mx-auto" style="max-width: 600px;">
            Monitora le tue attività con statistiche in tempo reale e grafici intuitivi
          </p>
        </div>

        <div class="row g-4">
          <div class="col-lg-8">
            <div class="card border-0 shadow-sm">
              <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h4 class="mb-0">Panoramica Generale</h4>
                  <div class="btn-group">
                    <button
                      class="btn btn-outline-primary"
                      :class="{ active: activeSection === 'auto' }"
                      @click="setActiveSection('auto')"
                    >
                      Auto FVG
                    </button>
                    <button
                      class="btn btn-outline-primary"
                      :class="{ active: activeSection === 'immobiliare' }"
                      @click="setActiveSection('immobiliare')"
                    >
                      Immobiliare FVG
                    </button>
                  </div>
                </div>

                <!-- Contenuto dinamico in base alla sezione attiva -->
                <div v-if="activeSection === 'auto'">
                  <img
                    :src="dashboardAutoImg"
                    alt="Dashboard Auto Preview"
                    class="img-fluid rounded"
                  >
                </div>
                <div v-else>
                  <img
                    :src="dashboardImmobiliImg"
                    alt="Dashboard Immobiliare Preview"
                    class="img-fluid rounded"
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="d-grid gap-4">
              <!-- KPI Cards -->
              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-graph-up"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Statistiche in Tempo Reale</h6>
                      <p class="text-muted small mb-0">
                        Monitora vendite, acquisti e transazioni
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-funnel"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Filtri Avanzati</h6>
                      <p class="text-muted small mb-0">
                        Ricerca dettagliata su tutti i moduli
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-clock-history"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Gestione Presenze</h6>
                      <p class="text-muted small mb-0">
                        Tracciamento turni e orari del personale
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-people-fill"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Gestione Collaborazioni</h6>
                      <p class="text-muted small mb-0">
                        Gestione partner e concessionari
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-download"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Export Dati</h6>
                      <p class="text-muted small mb-0">
                        Esportazione report in vari formati
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-person"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Vista Personalizzata</h6>
                      <p class="text-muted small mb-0">
                        Dashboard specifica per ogni ruolo
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-0 shadow-sm">
                <div class="card-body p-4">
                  <div class="d-flex align-items-center">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 me-3">
                      <i class="bi bi-calendar-check"></i>
                    </div>
                    <div>
                      <h6 class="mb-1">Calendario Integrato</h6>
                      <p class="text-muted small mb-0">
                        Pianificazione eventi e scadenze
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Funzionalità Principali -->
    <section class="features-overview py-7 bg-light">
      <div class="container">
        <div class="text-center">
          <span class="badge bg-primary-subtle text-primary mb-3">Funzionalità</span>
          <h2 class="display-5 fw-bold mb-3">Strumenti Avanzati</h2>
          <p class="lead text-muted">Tutto ciò che serve per gestire la tua attività</p>
        </div>

        <div class="row g-4 mt-4">
          <!-- Prima riga -->
          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-clipboard2-data"></i>
              </div>
              <h5>Riepilogo Preventivi</h5>
              <p class="text-muted">
                Monitora lo stato di tutti i preventivi: in scadenza,
                in attesa, accettati e rifiutati
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-graph-up-arrow"></i>
              </div>
              <h5>Statistiche Annunci</h5>
              <p class="text-muted">
                Visualizza gli annunci attivi, venduti e in sospeso
                per ogni categoria
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-arrow-left-right"></i>
              </div>
              <h5>Transazioni</h5>
              <p class="text-muted">
                Panoramica completa di acquisti, vendite, noleggi
                e mediazioni
              </p>
            </div>
          </div>

          <!-- Seconda riga -->
          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-bell-fill"></i>
              </div>
              <h5>Sistema Notifiche</h5>
              <p class="text-muted">
                Notifiche automatiche per documenti in scadenza nei prossimi 7 giorni
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-folder2-open"></i>
              </div>
              <h5>Gestione Documenti</h5>
              <p class="text-muted">
                Upload e gestione documenti aziendali, veicoli e clienti
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="text-center">
              <div class="feature-icon bg-primary-subtle text-primary rounded-circle mx-auto mb-3">
                <i class="bi bi-receipt"></i>
              </div>
              <h5>Fatturazione</h5>
              <p class="text-muted">
                Sistema completo di fatturazione con pagamenti multipli
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
      </div>
    </section>

    <!-- Aggiungere i tabs per la selezione -->
    <section class="management-features py-7">
      <div class="container">
        <div class="text-center mb-5">
          <ul class="nav nav-pills justify-content-center mb-5" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link active px-4 py-2"
                id="auto-tab"
                data-bs-toggle="tab"
                data-bs-target="#auto-content"
                type="button"
                role="tab"
                aria-selected="true"
              >
                Gestione Auto
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link px-4 py-2"
                id="immobiliare-tab"
                data-bs-toggle="tab"
                data-bs-target="#immobiliare-content"
                type="button"
                role="tab"
                aria-selected="false"
              >
                Gestione Immobiliare
              </button>
            </li>
          </ul>
        </div>

        <div class="tab-content">
          <!-- Contenuto Auto -->
          <div
            class="tab-pane fade show active"
            id="auto-content"
            role="tabpanel"
            aria-labelledby="auto-tab"
          >
            <div class="text-center mb-5">
              <span class="badge bg-primary-subtle text-primary mb-3">Gestione Auto</span>
              <h2 class="display-5 fw-bold mb-3">Gestione Parco Auto Completa</h2>
              <p class="lead text-muted">Tutto ciò che serve per gestire il tuo business automotive</p>
            </div>

            <div class="row g-4">
              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-car-front"></i>
                    </div>
                    <h4>Gestione Veicoli</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Schede tecniche dettagliate</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Storico manutenzioni</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Documenti e scadenze</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Foto e gallerie</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-people"></i>
                    </div>
                    <h4>Gestione Clienti</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Database clienti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Matching domanda/offerta</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Gestione appuntamenti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Storico trattative</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-file-text"></i>
                    </div>
                    <h4>Contratti e Documenti</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Template personalizzabili</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Gestione vendite</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Scadenziario pagamenti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Archivio digitale</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contenuto Immobiliare -->
          <div
            class="tab-pane fade"
            id="immobiliare-content"
            role="tabpanel"
            aria-labelledby="immobiliare-tab"
          >
            <div class="text-center mb-5">
              <span class="badge bg-primary-subtle text-primary mb-3">Gestione Immobiliare</span>
              <h2 class="display-5 fw-bold mb-3">Gestione Immobiliare Professionale</h2>
              <p class="lead text-muted">Strumenti avanzati per il tuo portfolio immobiliare</p>
            </div>

            <div class="row g-4">
              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-house"></i>
                    </div>
                    <h4>Gestione Immobili</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Schede immobili complete</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Planimetrie e documenti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Gestione certificazioni</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Foto e gallerie</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-people"></i>
                    </div>
                    <h4>Gestione Clienti</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Database clienti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Matching domanda/offerta</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Gestione appuntamenti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Storico trattative</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="feature-icon bg-primary-subtle text-primary rounded-3 mb-3">
                      <i class="bi bi-file-text"></i>
                    </div>
                    <h4>Contratti e Documenti</h4>
                    <ul class="list-unstyled">
                      <li><i class="bi bi-check2 text-primary me-2"></i>Template personalizzabili</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Gestione affitti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Scadenziario pagamenti</li>
                      <li><i class="bi bi-check2 text-primary me-2"></i>Archivio digitale</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Features',
  data() {
    return {
      activeSection: 'auto',
      dashboardAutoImg: `${window.location.origin}/images/DashboardAuto.png`,
      dashboardImmobiliImg: `${window.location.origin}/images/DashboardImmobili.png`
    }
  },
  methods: {
    setActiveSection(section) {
      this.activeSection = section
    }
  },
  mounted() {
    // Inizializza i tabs di Bootstrap
    import('bootstrap').then(bootstrap => {
      const tabElements = document.querySelectorAll('[data-bs-toggle="tab"]')
      tabElements.forEach(tabElement => {
        new bootstrap.Tab(tabElement)
      })
    })
  }
}
</script>

<style scoped>
.feature-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.card {
  transition: transform 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
}

/* Animazione per il cambio di contenuto */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animazioni per le sezioni */
.features-hero,
.dashboard-preview,
.auto-features,
.real-estate-features,
.integrations {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Ritardo progressivo per le sezioni */
.dashboard-preview { animation-delay: 0.2s; }
.auto-features { animation-delay: 0.4s; }
.real-estate-features { animation-delay: 0.6s; }
.integrations { animation-delay: 0.8s; }

/* Miglioramenti alle cards */
.card {
  border-radius: 12px;
  overflow: hidden;
}

.feature-icon {
  background: linear-gradient(45deg, var(--bs-primary) 0%, #4a90e2 100%);
  color: white !important;
  border-radius: 12px;
  width: 56px;
  height: 56px;
  font-size: 1.8rem;
}

/* Hover effects migliorati */
.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

/* Pattern overlay per hero section */
.features-hero {
  position: relative;
  overflow: hidden;
  background: #219ebc;
  background-image: linear-gradient(45deg, rgba(2, 48, 71, 0.8) 0%, transparent 100%);
}

.hero-pattern {
  top: 0;
  left: 0;
  opacity: 0.1;
  background-image: linear-gradient(30deg, #ffffff 12%, transparent 12.5%, transparent 87%, #ffffff 87.5%, #ffffff),
    linear-gradient(150deg, #ffffff 12%, transparent 12.5%, transparent 87%, #ffffff 87.5%, #ffffff),
    linear-gradient(30deg, #ffffff 12%, transparent 12.5%, transparent 87%, #ffffff 87.5%, #ffffff),
    linear-gradient(150deg, #ffffff 12%, transparent 12.5%, transparent 87%, #ffffff 87.5%, #ffffff),
    linear-gradient(60deg, #ffffff77 25%, transparent 25.5%, transparent 75%, #ffffff77 75%, #ffffff77);
  background-size: 80px 140px;
  background-position: 0 0, 0 0, 40px 70px, 40px 70px, 0 0;
}

/* Miglioramenti responsive */
@media (max-width: 768px) {
  .feature-icon {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }

  .display-4 {
    font-size: 2.5rem;
  }

  .display-5 {
    font-size: 2rem;
  }
}

/* Definire una classe utility per il padding verticale */
.py-7 {
  padding-top: 2rem !important;
  padding-bottom: 2rem !important;
}

/* Aumentare il margine tra le cards */
.row.g-4 {
  --bs-gutter-y: 2rem !important;
}

/* Aumentare lo spazio tra i titoli delle sezioni e il contenuto */
.text-center.mb-5 {
  margin-bottom: 3rem !important;
}

/* Aumentare lo spazio tra gli elementi nelle cards */
.card-body ul li {
  margin-bottom: 0.75rem;
}
.card-body ul li:last-child {
  margin-bottom: 0;
}

/* Aumentare lo spazio sotto le icone nelle cards */
.feature-icon.mb-3 {
  margin-bottom: 1.5rem !important;
}

/* Stili per i tabs */
.nav-pills .nav-link {
  border-radius: 50px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin: 0 0.5rem;
}

.nav-pills .nav-link:not(.active) {
  color: #2c3e50;
}

.nav-pills .nav-link:hover:not(.active) {
  background-color: #edf2f7;
}

/* Animazione per il cambio di tab */
.tab-pane {
  transition: all 0.3s ease-in-out;
}

.tab-pane.fade {
  transform: translateY(20px);
  opacity: 0;
}

.tab-pane.show {
  transform: translateY(0);
  opacity: 1;
}

.badge.bg-primary-subtle {
  background-color: rgba(142, 202, 230, 0.2) !important;
  color: #023047 !important;
}

.feature-icon.bg-primary-subtle {
  background-color: rgba(255, 183, 3, 0.1);
  color: #fb8500 !important;
}

.nav-pills .nav-link.active {
  background-color: #023047;
}

.btn-outline-primary {
  border-color: #ffb703;
  color: #ffb703;
}

.btn-outline-primary:hover,
.btn-outline-primary.active {
  background: #ffb703;
  color: #023047;
}

.features-hero {
  background: #219ebc;
}

.badge {
  background: #ffb703 !important;
  color: #023047 !important;
}

.feature-icon {
  color: #fb8500 !important;
}

.btn-outline-primary {
  border-color: #ffb703;
  color: #ffb703;
}

.btn-outline-primary:hover,
.btn-outline-primary.active {
  background: #ffb703;
  color: #023047;
}

.card {
  border-color: #8ecae6;
}

/* Hero section */
.features-hero {
  background: #219ebc !important; /* Azzurro medio */
  position: relative;
  overflow: hidden;
}

.hero-pattern {
  background-image: linear-gradient(135deg,
    rgba(142, 202, 230, 0.1) 25%,
    transparent 25%,
    transparent 50%,
    rgba(142, 202, 230, 0.1) 50%,
    rgba(142, 202, 230, 0.1) 75%,
    transparent 75%,
    transparent
  );
  background-size: 30px 30px;
}

/* Cards e box */
.card {
  border: 1px solid #8ecae6 !important; /* Azzurro chiaro */
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
  border-color: #219ebc !important;
  box-shadow: 0 10px 20px rgba(2, 48, 71, 0.1) !important;
}

/* Icone e badge */
.feature-icon {
  background: rgba(142, 202, 230, 0.1) !important;
  color: #fb8500 !important; /* Arancione */
}

.badge {
  background: #ffb703 !important; /* Giallo */
  color: #023047 !important; /* Blu scuro */
}

/* Tabs e navigazione */
.nav-pills .nav-link {
  color: #023047;
}

.nav-pills .nav-link.active {
  background: #219ebc !important;
  color: #fff !important;
}

.nav-pills .nav-link:hover:not(.active) {
  background: rgba(142, 202, 230, 0.1);
}

/* Bottoni outline */
.btn-outline-primary {
  border-color: #219ebc !important;
  color: #219ebc !important;
}

.btn-outline-primary:hover,
.btn-outline-primary.active {
  background: #219ebc !important;
  color: #fff !important;
}

/* Check icons */
.bi-check2.text-primary {
  color: #fb8500 !important;
}

/* Background sottili */
.bg-primary-subtle {
  background: rgba(142, 202, 230, 0.1) !important;
}

.dashboard-preview {
  background: #f8f9fa !important;
}
</style>
